import { AdmissionOfferStatus } from 'AdmissionOffer';
import { AoiRecordType } from 'ProgramAoi';
import { isPendingProgramApplication } from 'ProgramApplication';
import { ProgramInclusionStatus } from 'ProgramInclusion';
import {
    type CurrentUser,
    getAoiRecord,
    getCurrentlyPreventedFromSubmittingApplication,
    getIsDeclinedAdmissionOffer,
    getIsRejectedProgramApplication,
} from 'Users';
import { UnlockedTutorBotChatState } from 'TutorBotChat';
import { type BotPageState, InvalidBotPageState } from './TutorBotAiAdvisorChat.types';
import { hasBotPageAccess } from './TutorBotAiAdvisorRoutes';

// This function only gets called if we've already determined that there is not a global
// reason for TutorBot to be locked, as defined in determineTutorBotLockedState. See how this is
// used in useBotPageStatusConfig.
export function determineBotPageState(currentUser: CurrentUser | null): BotPageState {
    if (!currentUser || !hasBotPageAccess({ currentUser })) return InvalidBotPageState.NoAccess;

    const programApplication = getAoiRecord(currentUser, AoiRecordType.ProgramApplication);
    const admissionOffer = getAoiRecord(currentUser, AoiRecordType.AdmissionOffer);
    const programInclusion = getAoiRecord(currentUser, AoiRecordType.ProgramInclusion);

    const pendingApplicationForCurrentProgram = !!programApplication && isPendingProgramApplication(programApplication);
    const isRejectedProgramApplication = getIsRejectedProgramApplication(currentUser);
    const isDeclinedAdmissionOffer = getIsDeclinedAdmissionOffer(currentUser);

    const currentlyPreventedFromSubmittingApplication = getCurrentlyPreventedFromSubmittingApplication(currentUser);

    if (currentUser.canUseBotPageWithoutProgramInclusion) return UnlockedTutorBotChatState.IncludedOrGraduated;

    if (programInclusion?.status === ProgramInclusionStatus.Included) {
        if (programInclusion.current) {
            return UnlockedTutorBotChatState.IncludedOrGraduated;
        }

        // before cohort content unlocked
        return UnlockedTutorBotChatState.RegisteredNotCurrent;
    }

    if (programInclusion?.status === ProgramInclusionStatus.Graduated) {
        return UnlockedTutorBotChatState.IncludedOrGraduated;
    }

    if (programInclusion?.status === ProgramInclusionStatus.WillNotGraduate) {
        return UnlockedTutorBotChatState.ExpelledOrWithdrawn;
    }

    if (admissionOffer?.status === AdmissionOfferStatus.OfferedAdmission) {
        return UnlockedTutorBotChatState.OfferedAdmission;
    }

    if (isRejectedProgramApplication) {
        return UnlockedTutorBotChatState.NotOfferedAdmission;
    }

    if (isDeclinedAdmissionOffer) {
        return UnlockedTutorBotChatState.OfferExpired;
    }

    if (
        pendingApplicationForCurrentProgram &&
        programApplication.invitedToInterview &&
        !programApplication.interviewScheduled
    ) {
        return UnlockedTutorBotChatState.AppliedInterviewPending;
    }

    if (pendingApplicationForCurrentProgram) return UnlockedTutorBotChatState.Applied;

    if (currentlyPreventedFromSubmittingApplication) return UnlockedTutorBotChatState.CannotApply;

    return UnlockedTutorBotChatState.CanApply;
}
